import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import type { AdminApi, DebugSessionView, SdkLogLineView } from '../../api/client.ts';

const LEVELS: Array<SdkLogLineView['level']> = ['verbose', 'debug', 'info', 'warn', 'error'];

/** One line per log entry, the way it is exported: `time level [tag] message`. */
export const formatLogLine = (l: Pick<SdkLogLineView, 'at' | 'level' | 'tag' | 'message'>) => `${l.at} ${l.level.toUpperCase().padEnd(7)} [${l.tag}] ${l.message}`;

/**
 * The SDK's own verbose log of a debug session (BB-157), uploaded by the target's devices in an internal group and
 * redacted on the device: level filter, search, export as a text file.
 */
export function SdkLogTab({ environmentId, session }: { environmentId: string; session: DebugSessionView }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [level, setLevel] = useState<SdkLogLineView['level']>('verbose');
  const [q, setQ] = useState('');
  const logs = useQuery({
    queryKey: ['debug-sessions', environmentId, session.id, 'sdk-logs', level, q],
    queryFn: () => api.sdkLogs(environmentId, session.id, { ...(level !== 'verbose' ? { level } : {}), ...(q.trim() ? { q: q.trim() } : {}) }),
    refetchInterval: session.active ? 15_000 : false,
  });
  const text = (logs.data?.items ?? []).map(formatLogLine).join('\n');
  return (
    <section aria-label="SDK log" className="panel">
      <h3>SDK log</h3>
      <p className="hint">The device uploads its SDK log every 30 seconds while the session lasts. Tokens, emails, phone numbers and attribute values are removed on the device.</p>
      <div className="toolbar">
        <label>
          Level{' '}
          <select value={level} onChange={(e) => setLevel(e.target.value as SdkLogLineView['level'])}>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l === 'verbose' ? 'All levels' : `${l} and above`}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {logs.data?.items.length ? (
          <a className="button" href={`data:text/plain;charset=utf-8,${encodeURIComponent(`${text}\n`)}`} download={`sdk-log-${session.id}.txt`}>
            Export .txt
          </a>
        ) : null}
      </div>
      {logs.isError && <p role="alert">The SDK log could not be loaded.</p>}
      {logs.data &&
        (logs.data.items.length ? (
          <pre aria-label="SDK log lines" className="sdk-log">
            {text}
          </pre>
        ) : (
          <p className="hint">{level === 'verbose' && !q ? 'No SDK log yet: open the app on a device of an internal group.' : 'No lines match.'}</p>
        ))}
      {logs.data?.truncated && <p className="hint">Only the first 5,000 lines are shown; narrow by level or search.</p>}
    </section>
  );
}
